import { useEffect, useState } from 'react';
import {
  useDeleteAccumulationMutation,
  useDeleteAccumulationUploadMutation,
  useGetAccumulationQuery,
  useLazyExportAccumulationXlsxQuery,
  useListAccumulationsQuery,
} from '@shared/api';
import type { AccumLedger } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { AccumulationBindingPanel } from './AccumulationBindingPanel';
import { summaryWarnings } from './accumulationLabels';
import { AccumulationSeedForm } from './AccumulationSeedForm';
import { EntriesPanel } from './EntriesPanel';
import { MonthlyPackUploadForm } from './MonthlyPackUploadForm';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import {
  CheckRow,
  CrossLink,
  ErrMargin,
  Grid,
  Muted,
  ObjName,
  Row,
  Stack,
  TableTitle,
} from './styled';
import { UploadsTable } from './UploadsTable';

export function AccumulationsView() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  const listQ = useListAccumulationsQuery(undefined, { pollingInterval: 8000 });
  const [deleteAccum] = useDeleteAccumulationMutation();
  const [deleteUpload] = useDeleteAccumulationUploadMutation();
  const detailQ = useGetAccumulationQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportAccumulationXlsxQuery();

  // Поллинг детали, пока есть незавершённые загрузки.
  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const busy = detailQ.data?.uploads.some(
      (u) => u.status === 'pending' || u.status === 'processing',
    );
    setPoll(busy ? 2500 : 0);
  }, [selectedId, detailQ.data?.uploads]);

  async function handleDeleteLedger(id: string) {
    try {
      await deleteAccum(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // список обновится при поллинге
    }
  }

  async function handleDeleteUpload(id: string) {
    try {
      await deleteUpload(id).unwrap();
    } catch {
      // обновится при поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accumulation_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить книгу XLSX.');
      // eslint-disable-next-line no-console
      console.error('exportAccumulationXlsx failed', err);
    }
  }

  const ledgers: AccumLedger[] = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const detailWarnings = detail
    ? Array.from(new Set(detail.uploads.flatMap(summaryWarnings)))
    : [];
  const isAuthor =
    !detail?.ledger.user_id ||
    !currentUserId ||
    detail.ledger.user_id === currentUserId;

  return (
    <Stack>
      <Row>
        <Muted>
          Персистентная лента «данные ПА» по договору. Один раз загрузите базовую книгу, далее —
          ежемесячные пачки первичных актов.
        </Muted>
        <CrossLink to="/agents/smeta/registers">← Реестры (накладные/КС-2)</CrossLink>
      </Row>

      <AccumulationSeedForm onSeeded={(ledgerId) => setSelectedId(ledgerId)} />

      <Grid>
        <Stack>
          <Muted>Лент: {ledgers.length}</Muted>
          {ledgers.map((l) => (
            <CheckRow key={l.id} $active={l.id === selectedId} onClick={() => setSelectedId(l.id)}>
              <ObjName>{l.title || l.contract_no}</ObjName>
              <Muted>
                {l.contract_no}
                {l.object_name ? ` · ${l.object_name}` : ''} ·{' '}
                {new Date(l.updated_at).toLocaleDateString('ru-RU')}
              </Muted>
            </CheckRow>
          ))}
          {ledgers.length === 0 && <Muted>Лент пока нет. Заведите ленту из книги выше.</Muted>}
        </Stack>

        <div>
          {!selectedId && <Muted>Выберите ленту слева.</Muted>}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (
            <Card>
              <Row>
                <div>
                  <ObjName>{detail.ledger.title || detail.ledger.contract_no}</ObjName>
                  <Muted>
                    {detail.ledger.contract_no}
                    {detail.ledger.object_name ? ` · ${detail.ledger.object_name}` : ''} · расценок:{' '}
                    {detail.rate_count} · строк ленты: {detail.entry_count}
                    {detail.entries_missing_mark > 0
                      ? ` · без марки РД: ${detail.entries_missing_mark}`
                      : ''}
                  </Muted>
                </div>
                <Row>
                  <Button
                    $variant="secondary"
                    $size="sm"
                    type="button"
                    onClick={() => handleExport(detail.ledger.id)}
                    disabled={exporting || detail.entry_count === 0}
                  >
                    {exporting ? 'Готовлю…' : 'Книга накопительного учёта (XLSX)'}
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={!can_write || !isAuthor}
                    title={
                      !can_write
                        ? 'Только чтение'
                        : !isAuthor
                          ? 'Удалить ленту может только её автор'
                          : undefined
                    }
                    onClick={() => handleDeleteLedger(detail.ledger.id)}
                  >
                    Удалить ленту
                  </Button>
                </Row>
              </Row>
              {exportError && <ErrMargin>{exportError}</ErrMargin>}

              <AccumulationBindingPanel
                ledgerId={detail.ledger.id}
                initialContractId={detail.ledger.statut_contract_id}
                initialProjectId={detail.ledger.project_id}
                canWrite={can_write}
              />

              <MonthlyPackUploadForm ledgerId={detail.ledger.id} canWrite={can_write} />

              <ParseWarningsBanner warnings={detailWarnings} />

              <TableTitle>
                Загрузки <Badge $tone="neutral">{detail.uploads.length}</Badge>
              </TableTitle>
              <UploadsTable
                uploads={detail.uploads}
                canWrite={can_write}
                onDelete={handleDeleteUpload}
              />

              <EntriesPanel ledgerId={detail.ledger.id} canWrite={can_write} />
            </Card>
          )}
        </div>
      </Grid>
    </Stack>
  );
}
